import { useAuthorization } from '@/features/authorization'
import { useLaunchMode } from '@/features/auth/launchContext'
import { isCaseConnectEnabled } from '@/config/uiConfig'
import { useUiConfig } from '@/config/uiConfigHooks'
import { useOrgNpiAvailability } from '@/hooks/useOrgNpiAvailability'
import { tryImdbUserIdFromBearerJwt } from '@/services/npiInUseCheck'
import {
  FREE_TEXT_INVALID_MESSAGE,
  NPI_INVALID_MESSAGE,
  PHONE_INVALID_MESSAGE,
  PHONE_MAX_LENGTH,
  PHONE_TOO_LONG_MESSAGE,
  ROLE_TITLE_INVALID_MESSAGE,
  ROLE_TITLE_MAX_LENGTH,
  ROLE_TITLE_TOO_LONG_MESSAGE,
  isValidFreeText,
  isValidNpi,
  isValidPhone,
  isValidRoleTitle,
  isWithinLength,
} from '@/utils/freeTextValidation'
import {
  MIDDLE_NAME_FIELD_MAX_LENGTH,
  NAME_FIELD_MAX_LENGTH,
  USER_NAME_INVALID_MESSAGE,
  composeDisplayName,
  isValidUserNameIfChanged,
  middleNameFieldLengthError,
  nameFieldLengthError,
} from '@/utils/usernameValidation'
import { parseApiErrorMessage } from '@/utils/parseApiErrorMessage'
import { useAuth0 } from '@auth0/auth0-react'
import type { User } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'
import { Pen } from 'flowbite-react-icons/outline'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { InflectionLogo } from './InflectionLogo'

function getNpiFromAuth0User(user: User | undefined): string {
  if (!user) return ''
  const record = user as Record<string, unknown>
  const claim =
    record['https://inflectionmedicine.com/npi'] ??
    record['https://provider.dev.inflectionmedicine.com/npi']
  return typeof claim === 'string' ? claim.trim() : ''
}

/**
 * Body for PATCH /auth/me. All fields optional; at least one required.
 * Name parts are each ≤ 32 chars (the `practitioner` columns they map to);
 * `phone` ≤ 40 chars, `roleTitle` ≤ 64 chars, `npi` ≤ 16 chars (enforced server-side).
 */
type ProfileUpdateRequest = {
  firstName?: string
  middleName?: string
  lastName?: string
  npi?: string
  phone?: string
  roleTitle?: string
}

interface NavbarProps {
  userAvatar?: string
  userName?: string
  onLogout?: () => void
  showNavLinks?: boolean
  organizationName?: string | null
  onCreateNewOrder?: () => void
}

/**
 * Main navigation bar component
 * Based on Figma design: Navbar (one level)
 * Note: Hidden when running in Epic embedded browser
 */
export const Navbar: React.FC<NavbarProps> = ({
  userAvatar,
  userName,
  onLogout,
  showNavLinks = true,
  organizationName = null,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { hasRole, hasPermission, refreshPermissions } = useAuthorization()
  const { user, getAccessTokenSilently } = useAuth0()
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const [accountEditing, setAccountEditing] = useState(false)
  const [editFirstName, setEditFirstName] = useState('')
  const [editMiddleName, setEditMiddleName] = useState('')
  const [editLastName, setEditLastName] = useState('')
  const [editNpi, setEditNpi] = useState('')
  const [editPhone, setEditPhone] = useState('')
  const [editRoleTitle, setEditRoleTitle] = useState('')
  const [saveLoading, setSaveLoading] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [selfExcludeUserId, setSelfExcludeUserId] = useState<string | null>(null)
  /**
   * Self profile fetched from GET /auth/me/profile. Source for phone/roleTitle and for
   * the name parts, none of which are in Auth0 claims — the claims only carry the
   * composed display name, and the parts cannot be recovered from it by splitting.
   */
  const [fetchedProfile, setFetchedProfile] = useState<{
    name: string
    firstName: string
    middleName: string
    lastName: string
    phone: string
    roleTitle: string
  } | null>(null)
  /** After PATCH until Auth0 token refresh — initials, display name, NPI */
  const [displayOverride, setDisplayOverride] = useState<{
    displayName: string
    firstName: string
    middleName: string
    lastName: string
    npi: string
    phone: string
    roleTitle: string
  } | null>(null)
  const lastPatchBodyRef = useRef<ProfileUpdateRequest | null>(null)
  /**
   * Phone / roleTitle values the edit form was seeded with when editing opened.
   * If GET /auth/me/profile resolves *after* the user opens the edit panel, the
   * fields were seeded from an empty (not-yet-loaded) baseline. We use this to
   * re-seed only untouched fields once the profile arrives, so a later save does
   * not PATCH phone/roleTitle back to empty and wipe persisted contact data.
   */
  const editContactSeedRef = useRef<{
    firstName: string
    middleName: string
    lastName: string
    phone: string
    roleTitle: string
  } | null>(null)
  const isOrgAdmin = hasRole('org_admin')
  // Case Connect visibility. A case-only (org-less) user resolves case:view
  // but NOT order:view — they must NOT see the order Workspace link (it would
  // dead-end them), and their logo/home is /cases.
  const canViewCases = hasPermission('case:view')
  const canViewOrders = hasPermission('order:view')
  const isCaseOnly = canViewCases && !canViewOrders
  const homePath = isCaseOnly ? '/cases' : '/orders'
  // The "Case Connect" nav link is gated by the per-org feature flag FOR ORG
  // MEMBERS only. An org-less (case-only) participant has NO client config (the
  // flag resolves false), so they are exempt: they always see the link — it is
  // their only path to their invited case (permission-gated server-side).
  const { config: uiConfig } = useUiConfig()
  const showCaseConnectLink = canViewCases && (isCaseOnly || isCaseConnectEnabled(uiConfig))

  const apiBaseUrl = useMemo(
    () => import.meta.env['VITE_PROVIDER_API_URL'] || import.meta.env['VITE_API_BASE_URL'],
    []
  )
  const isLoginPage = location.pathname === '/login'

  // Use launch context (URL/session/LS), not `location.state` — router state is
  // easy to get wrong on first paint (stale/ambiguous) and can hide the bar
  // until a full reload. Epic SMART in-app view keeps `mode === 'epic_smart'`.
  const { mode: launchMode } = useLaunchMode()

  const baselineLoginEmail = (typeof user?.email === 'string' ? user.email : '').trim()
  const baselineNpi = getNpiFromAuth0User(user)
  const displayNameForInitials =
    displayOverride?.displayName !== undefined
      ? displayOverride.displayName.trim() || userName || user?.name
      : userName || user?.name
  const initialsSourceDisplay =
    typeof displayNameForInitials === 'string' && displayNameForInitials.trim()
      ? displayNameForInitials.trim()
      : '—'
  const loginEmailDisplay = baselineLoginEmail || '—'
  const npiDisplay =
    displayOverride !== null
      ? displayOverride.npi.trim() || '—'
      : baselineNpi || '—'

  // Phone and medical title are not synced to Auth0 claims, so we source them from
  // GET /auth/me/profile (fetchedProfile). The optimistic displayOverride takes
  // precedence right after a save until the fetched profile is refreshed.
  const baselinePhone = fetchedProfile?.phone ?? ''
  const baselineRoleTitle = fetchedProfile?.roleTitle ?? ''
  const phoneDisplay = displayOverride !== null ? displayOverride.phone.trim() || '—' : baselinePhone || '—'
  const roleTitleDisplay =
    displayOverride !== null ? displayOverride.roleTitle.trim() || '—' : baselineRoleTitle || '—'

  /** Same sources as read-only Name/NPI — avoids stale Auth0 baselines after PATCH when token refresh fails */
  const baselineNpiEffective = displayOverride !== null ? displayOverride.npi.trim() : baselineNpi
  const baselinePhoneEffective = displayOverride !== null ? displayOverride.phone.trim() : baselinePhone
  const baselineRoleTitleEffective =
    displayOverride !== null ? displayOverride.roleTitle.trim() : baselineRoleTitle

  // Name parts come only from GET /auth/me/profile: Auth0 carries the composed
  // display name, and splitting it back apart is exactly the guess this form removes.
  const baselineFirstNameEffective =
    displayOverride !== null ? displayOverride.firstName.trim() : (fetchedProfile?.firstName ?? '')
  const baselineMiddleNameEffective =
    displayOverride !== null ? displayOverride.middleName.trim() : (fetchedProfile?.middleName ?? '')
  const baselineLastNameEffective =
    displayOverride !== null ? displayOverride.lastName.trim() : (fetchedProfile?.lastName ?? '')

  const selfNpiAvailability = useOrgNpiAvailability({
    npi: editNpi,
    excludeUserId: selfExcludeUserId,
    baselineNpi: baselineNpiEffective,
    enabled: accountEditing,
  })

  useEffect(() => {
    if (!accountEditing) {
      setSelfExcludeUserId(null)
      return
    }
    let cancelled = false
    void getAccessTokenSilently()
      .then((token) => {
        if (!cancelled) {
          setSelfExcludeUserId(tryImdbUserIdFromBearerJwt(token))
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSelfExcludeUserId(null)
        }
      })
    return () => {
      cancelled = true
    }
  }, [accountEditing, getAccessTokenSilently])

  // Fetch the self profile (phone / roleTitle are not in Auth0 claims) so the
  // read-only view and edit form show the persisted values on load. Refetches
  // when the authenticated user changes.
  useEffect(() => {
    if (launchMode === 'epic_smart') return
    if (!user?.sub) return
    // Clear any prior user's profile immediately so an account switch never briefly
    // shows the previous user's phone / medical title before the new fetch resolves.
    setFetchedProfile(null)
    let cancelled = false
    void (async () => {
      try {
        const token = await getAccessTokenSilently()
        const response = await fetch(`${apiBaseUrl}/auth/me/profile`, {
          headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
        })
        if (!response.ok) {
          // 401/403/404/500 — leave phone/title empty; do not disrupt the session.
          Sentry.captureMessage('GET /auth/me/profile failed', {
            level: 'warning',
            extra: { status: response.status },
          })
          return
        }
        const data = (await response.json()) as {
          name?: string | null
          firstName?: string | null
          middleName?: string | null
          lastName?: string | null
          phone?: string | null
          roleTitle?: string | null
        }
        if (cancelled) return
        setFetchedProfile({
          name: (data.name ?? '').trim(),
          firstName: (data.firstName ?? '').trim(),
          middleName: (data.middleName ?? '').trim(),
          lastName: (data.lastName ?? '').trim(),
          phone: (data.phone ?? '').trim(),
          roleTitle: (data.roleTitle ?? '').trim(),
        })
      } catch (e) {
        if (!cancelled) Sentry.captureException(e, { tags: { operation: 'navbar.me.profile.get' } })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [user?.sub, launchMode, apiBaseUrl, getAccessTokenSilently])

  useEffect(() => {
    if (!userMenuOpen) {
      setAccountEditing(false)
      setSaveError(null)
      lastPatchBodyRef.current = null
    }
  }, [userMenuOpen])

  useEffect(() => {
    setDisplayOverride(null)
  }, [user?.sub, user?.email, user?.name, baselineNpi])

  // If GET /auth/me/profile resolves while the account edit panel is already open,
  // the phone/roleTitle inputs were seeded from an empty baseline. Re-seed any field
  // the user hasn't touched (still equal to the stale seed) with the freshly-loaded
  // value so saving another field can't PATCH phone/roleTitle back to '' and wipe the
  // persisted contact data on the server. Intentional user edits (value diverged from
  // the seed) are preserved.
  useEffect(() => {
    if (!accountEditing) return
    const seed = editContactSeedRef.current
    if (!seed) return
    const next = {
      firstName: baselineFirstNameEffective,
      middleName: baselineMiddleNameEffective,
      lastName: baselineLastNameEffective,
      phone: baselinePhoneEffective,
      roleTitle: baselineRoleTitleEffective,
    }
    if (
      seed.firstName === next.firstName &&
      seed.middleName === next.middleName &&
      seed.lastName === next.lastName &&
      seed.phone === next.phone &&
      seed.roleTitle === next.roleTitle
    ) {
      return
    }
    if (seed.firstName !== next.firstName) {
      setEditFirstName((current) => (current === seed.firstName ? next.firstName : current))
    }
    if (seed.middleName !== next.middleName) {
      setEditMiddleName((current) => (current === seed.middleName ? next.middleName : current))
    }
    if (seed.lastName !== next.lastName) {
      setEditLastName((current) => (current === seed.lastName ? next.lastName : current))
    }
    if (seed.phone !== next.phone) {
      setEditPhone((current) => (current === seed.phone ? next.phone : current))
    }
    if (seed.roleTitle !== next.roleTitle) {
      setEditRoleTitle((current) => (current === seed.roleTitle ? next.roleTitle : current))
    }
    editContactSeedRef.current = next
  }, [
    accountEditing,
    baselineFirstNameEffective,
    baselineMiddleNameEffective,
    baselineLastNameEffective,
    baselinePhoneEffective,
    baselineRoleTitleEffective,
  ])

  useEffect(() => {
    if (!userMenuOpen) return
    function handlePointerDown(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      if (accountEditing) {
        setAccountEditing(false)
        setSaveError(null)
        lastPatchBodyRef.current = null
        setEditFirstName(baselineFirstNameEffective)
        setEditMiddleName(baselineMiddleNameEffective)
        setEditLastName(baselineLastNameEffective)
        setEditNpi(baselineNpiEffective)
        setEditPhone(baselinePhoneEffective)
        setEditRoleTitle(baselineRoleTitleEffective)
      } else {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [
    userMenuOpen,
    accountEditing,
    baselineFirstNameEffective,
    baselineMiddleNameEffective,
    baselineLastNameEffective,
    baselineNpiEffective,
    baselinePhoneEffective,
    baselineRoleTitleEffective,
  ])

  if (launchMode === 'epic_smart') {
    return null
  }

  const orgNameFromAuth0 =
    user && typeof user.org_name === 'string' && user.org_name.trim().length > 0 ? user.org_name.trim() : null
  const resolvedOrganizationName =
    (typeof organizationName === 'string' && organizationName.trim().length > 0
      ? organizationName.trim()
      : null) ?? orgNameFromAuth0
  // Suppress the org chip for a case-only (org-less, Path B) participant: they
  // have no home org, so any client-config/Auth0 org name would be another
  // org's branding shown as theirs. Their per-case org is shown on the case
  // card / case page instead. Uses the reliable permission-based signal
  // (isCaseOnly) rather than a token org_id claim, which an invitee's token may
  // still carry even though they have no IMDB org membership.
  const displayOrganizationName = isCaseOnly ? null : resolvedOrganizationName

  const handleLogoClick = () => {
    navigate(homePath)
  }

  const navLinkClassName = ({ isActive }: { isActive: boolean }) =>
    ['text-sm font-medium transition-colors', isActive ? 'text-indigo-600' : 'text-gray-600 hover:text-gray-900'].join(
      ' '
    )

  const getInitials = (name: string | undefined): string => {
    if (!name || !name.trim()) return '?'
    const parts = name.trim().split(/\s+/)
    const first = parts[0]?.[0] ?? ''
    const second = parts[1]?.[0] ?? ''
    return (first + second).toUpperCase().slice(0, 2)
  }

  const editFirstNameChanged = editFirstName.trim() !== baselineFirstNameEffective
  const editMiddleNameChanged = editMiddleName.trim() !== baselineMiddleNameEffective
  const editLastNameChanged = editLastName.trim() !== baselineLastNameEffective
  const editNameChanged = editFirstNameChanged || editMiddleNameChanged || editLastNameChanged

  const isDirty =
    editNameChanged ||
    editNpi.trim() !== baselineNpiEffective ||
    editPhone.trim() !== baselinePhoneEffective ||
    editRoleTitle.trim() !== baselineRoleTitleEffective

  // Length is only enforced on fields the user changed (mirrors isValidUserNameIfChanged),
  // so a legacy over-length value doesn't block an NPI-only / phone-only profile edit.
  const editFirstNameLengthError = editFirstNameChanged ? nameFieldLengthError(editFirstName) : undefined
  const editMiddleNameLengthError = editMiddleNameChanged ? middleNameFieldLengthError(editMiddleName) : undefined
  const editLastNameLengthError = editLastNameChanged ? nameFieldLengthError(editLastName) : undefined

  const editFirstNameInvalid =
    !isValidUserNameIfChanged(editFirstName, baselineFirstNameEffective) || Boolean(editFirstNameLengthError)
  const editMiddleNameInvalid =
    !isValidUserNameIfChanged(editMiddleName, baselineMiddleNameEffective) || Boolean(editMiddleNameLengthError)
  const editLastNameInvalid =
    !isValidUserNameIfChanged(editLastName, baselineLastNameEffective) || Boolean(editLastNameLengthError)
  const editNameInvalid = editFirstNameInvalid || editMiddleNameInvalid || editLastNameInvalid
  /** First matching name error, so the shared save banner has something to show. */
  const editNameErrorMessage = editNameInvalid
    ? (editFirstNameLengthError ??
      editMiddleNameLengthError ??
      editLastNameLengthError ??
      USER_NAME_INVALID_MESSAGE)
    : undefined

  // Trim to match the PATCH body (which trims NPI) so a whitespace-only value isn't flagged invalid
  // and can't get the Save button stuck disabled.
  const editNpiInvalid = !isValidNpi(editNpi.trim())
  const editPhoneInvalid =
    !isValidFreeText(editPhone) || !isWithinLength(editPhone, PHONE_MAX_LENGTH) || !isValidPhone(editPhone)
  const editRoleTitleInvalid =
    !isValidFreeText(editRoleTitle) ||
    !isValidRoleTitle(editRoleTitle) ||
    !isWithinLength(editRoleTitle, ROLE_TITLE_MAX_LENGTH)
  const editFieldsValid = !editNameInvalid && !editNpiInvalid && !editPhoneInvalid && !editRoleTitleInvalid

  const showUserAvatar = Boolean(userName || userAvatar || user?.email || user?.name || user?.sub)

  /** Name after applying a partial PATCH body over the current baselines. */
  const effectiveNameParts = (body: ProfileUpdateRequest) => {
    const first = body.firstName !== undefined ? body.firstName.trim() : baselineFirstNameEffective
    const middle = body.middleName !== undefined ? body.middleName.trim() : baselineMiddleNameEffective
    const last = body.lastName !== undefined ? body.lastName.trim() : baselineLastNameEffective
    return { first, middle, last, display: composeDisplayName(first, middle, last) }
  }

  /** Profile PATCH: name parts, NPI, phone, and medical title (`roleTitle`) — not login email. */
  const buildPatchBody = (): ProfileUpdateRequest | null => {
    const nextFirstName = editFirstName.trim()
    const nextMiddleName = editMiddleName.trim()
    const nextLastName = editLastName.trim()
    const nextNpi = editNpi.trim()
    const nextPhone = editPhone.trim()
    const nextRoleTitle = editRoleTitle.trim()
    const body: ProfileUpdateRequest = {}
    if (nextFirstName !== baselineFirstNameEffective) body.firstName = nextFirstName
    if (nextMiddleName !== baselineMiddleNameEffective) body.middleName = nextMiddleName
    if (nextLastName !== baselineLastNameEffective) body.lastName = nextLastName
    if (nextNpi !== baselineNpiEffective) body.npi = nextNpi
    if (nextPhone !== baselinePhoneEffective) body.phone = nextPhone
    if (nextRoleTitle !== baselineRoleTitleEffective) body.roleTitle = nextRoleTitle
    if (Object.keys(body).length === 0) return null
    const effectiveNpi = body.npi !== undefined ? body.npi.trim() : baselineNpiEffective
    const effectivePhone = body.phone !== undefined ? body.phone.trim() : baselinePhoneEffective
    const effectiveRoleTitle = body.roleTitle !== undefined ? body.roleTitle.trim() : baselineRoleTitleEffective
    if (!effectiveNameParts(body).display && !effectiveNpi && !effectivePhone && !effectiveRoleTitle) {
      return null
    }
    return body
  }

  const applyPatchMe = async (body: ProfileUpdateRequest) => {
    const token = await getAccessTokenSilently()
    const response = await fetch(`${apiBaseUrl}/auth/me`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })

    if (response.ok) {
      lastPatchBodyRef.current = null
      // Only NPI changes can alter the backend identity/roles. Name, phone and
      // roleTitle never affect authorization, so skip the forced re-auth: a
      // cacheMode:'off' token fetch triggers a silent-auth iframe that can log
      // the user out when the SSO session can't silently renew (GEN-3078
      // regression). Non-NPI edits rely on the optimistic displayOverride below.
      if (body.npi !== undefined) {
        try {
          await getAccessTokenSilently({ cacheMode: 'off' })
        } catch {
          // Session still valid; UI override covers stale claims briefly
        }
        // GEN-3078: when NPI changes, the backend may resolve a different internal
        // user_id on the next request, which can change the caller's roles and
        // permissions. Refresh authorization state so the UI (Admin link, route
        // guards) reflects the new identity instead of the prior NPI's roles.
        try {
          await refreshPermissions()
        } catch {
          // Best-effort; the next route navigation will trigger a fresh fetch
        }
      }
      // Unchanged fields must follow effective baselines (include prior displayOverride), not raw Auth0,
      // or a partial PATCH reverts the other field when the token is still stale.
      const nextName = effectiveNameParts(body)
      const nextNpiVal = body.npi !== undefined ? body.npi.trim() : baselineNpiEffective
      const nextPhoneVal = body.phone !== undefined ? body.phone.trim() : baselinePhoneEffective
      const nextRoleTitleVal =
        body.roleTitle !== undefined ? body.roleTitle.trim() : baselineRoleTitleEffective
      setDisplayOverride({
        displayName: nextName.display,
        firstName: nextName.first,
        middleName: nextName.middle,
        lastName: nextName.last,
        npi: nextNpiVal,
        phone: nextPhoneVal,
        roleTitle: nextRoleTitleVal,
      })
      // Keep the fetched-profile baseline in sync so phone/title survive even if
      // displayOverride is later cleared (e.g. the NPI-change identity refresh).
      setFetchedProfile({
        name: nextName.display,
        firstName: nextName.first,
        middleName: nextName.middle,
        lastName: nextName.last,
        phone: nextPhoneVal,
        roleTitle: nextRoleTitleVal,
      })
      setAccountEditing(false)
      setSaveError(null)
      return
    }

    lastPatchBodyRef.current = body
    const message = await parseApiErrorMessage(response)

    if (response.status === 502) {
      setSaveError(
        `${message} If this persists, try again or contact support — your account data may be temporarily out of sync.`
      )
    } else {
      setSaveError(message)
    }

    Sentry.captureMessage('PATCH /auth/me failed', {
      level: 'warning',
      extra: { status: response.status, body },
    })
  }

  const handleSaveProfile = () =>
    Sentry.startSpan({ op: 'ui.click', name: 'Save account profile' }, async () => {
      setSaveError(null)
      const body = buildPatchBody()
      if (!body) {
        // buildPatchBody returns null both when nothing changed and when edits clear all
        // fields — only the latter leaves the form dirty with Save enabled.
        setSaveError(isDirty ? 'Provide a name, NPI, phone, and/or medical title.' : 'Nothing to save.')
        return
      }
      const nextName = effectiveNameParts(body)
      if (editNameChanged && (!nextName.first || !nextName.last)) {
        setSaveError('First and last name are both required.')
        return
      }
      if (!editFieldsValid) {
        if (editNameInvalid) {
          setSaveError(editNameErrorMessage ?? USER_NAME_INVALID_MESSAGE)
        } else if (editPhoneInvalid) {
          setSaveError(
            !isWithinLength(editPhone, PHONE_MAX_LENGTH)
              ? PHONE_TOO_LONG_MESSAGE
              : !isValidFreeText(editPhone)
                ? FREE_TEXT_INVALID_MESSAGE
                : PHONE_INVALID_MESSAGE
          )
        } else if (editRoleTitleInvalid) {
          setSaveError(
            !isWithinLength(editRoleTitle, ROLE_TITLE_MAX_LENGTH)
              ? ROLE_TITLE_TOO_LONG_MESSAGE
              : isValidFreeText(editRoleTitle)
                ? ROLE_TITLE_INVALID_MESSAGE
                : FREE_TEXT_INVALID_MESSAGE
          )
        } else if (editNpiInvalid) {
          setSaveError(NPI_INVALID_MESSAGE)
        } else {
          setSaveError(FREE_TEXT_INVALID_MESSAGE)
        }
        return
      }
      const effectiveNpi = body.npi !== undefined ? body.npi.trim() : baselineNpiEffective
      const effectivePhone = body.phone !== undefined ? body.phone.trim() : baselinePhoneEffective
      const effectiveRoleTitle =
        body.roleTitle !== undefined ? body.roleTitle.trim() : baselineRoleTitleEffective
      if (!nextName.display && !effectiveNpi && !effectivePhone && !effectiveRoleTitle) {
        setSaveError('Provide a name, NPI, phone, and/or medical title.')
        return
      }
      if (body.npi !== undefined) {
        const npiCheck = await selfNpiAvailability.validateBeforeSubmit()
        if (!npiCheck.ok) {
          setSaveError(npiCheck.message)
          return
        }
      }
      setSaveLoading(true)
      try {
        await applyPatchMe(body)
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Save failed'
        setSaveError(msg)
        Sentry.captureException(e, { tags: { operation: 'auth.patch_me' } })
      } finally {
        setSaveLoading(false)
      }
    })

  const handleRetryPatch = () => {
    const body = lastPatchBodyRef.current
    if (!body) return
    void Sentry.startSpan({ op: 'ui.click', name: 'Retry PATCH /auth/me' }, async () => {
      setSaveLoading(true)
      setSaveError(null)
      try {
        await applyPatchMe(body)
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Save failed'
        setSaveError(msg)
        Sentry.captureException(e, { tags: { operation: 'auth.patch_me.retry' } })
      } finally {
        setSaveLoading(false)
      }
    })
  }

  const handleCancelEdit = () => {
    setAccountEditing(false)
    setSaveError(null)
    lastPatchBodyRef.current = null
    setEditFirstName(baselineFirstNameEffective)
    setEditMiddleName(baselineMiddleNameEffective)
    setEditLastName(baselineLastNameEffective)
    setEditNpi(baselineNpiEffective)
    setEditPhone(baselinePhoneEffective)
    setEditRoleTitle(baselineRoleTitleEffective)
  }

  return (
    <div className="border-b border-gray-200 bg-white">
      <div className="flex h-20 items-center justify-between gap-8 px-6">
        {/* Logo (+ brand wordmark for a case-only, org-less user — they have no
            org chip, so show the Inflection Medicine product name next to the
            logo like the prototype). */}
        <button
          onClick={handleLogoClick}
          className="flex cursor-pointer items-center gap-2.5 focus:outline-none"
          aria-label={isCaseOnly ? 'Go to Case Connect' : 'Go to Orders'}
        >
          {isLoginPage ? (
            <InflectionLogo size="md" />
          ) : (
            <img src="/icons/Inflection_Icon_Dark.svg" alt="Inflection Medicine" className="h-8 w-auto" />
          )}
          {isCaseOnly && !isLoginPage && (
            <span className="flex flex-col leading-none text-[18px] uppercase tracking-[0.15em]">
              <span className="font-bold text-brand-900 dark:text-white">INFLECTION</span>
              <span className="font-medium text-brand-700 dark:text-brand-300">MEDICINE</span>
            </span>
          )}
        </button>

        {/* Organization Name Label */}
        {displayOrganizationName && (
          <div
            className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700"
            aria-label={`Current organization: ${displayOrganizationName}`}
          >
            <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            <span className="max-w-xs truncate">{displayOrganizationName}</span>
          </div>
        )}

        {/* Nav Links */}
        {showNavLinks ? (
          <nav className="flex flex-1 items-center justify-center gap-6" aria-label="Primary navigation">
            {!isCaseOnly && (
              <NavLink to="/orders" className={navLinkClassName}>
                Workspace
              </NavLink>
            )}
            {showCaseConnectLink && (
              <NavLink to="/cases" className={navLinkClassName}>
                Case Connect
              </NavLink>
            )}
            {isOrgAdmin && (
              <NavLink to="/admin" className={navLinkClassName}>
                Admin
              </NavLink>
            )}
          </nav>
        ) : (
          <div className="flex-1" />
        )}

        {/* Right side actions */}
        <div className="flex items-center gap-1">
          {/* Dark mode toggle (optional - for future use) */}
          {/* <button
            className="flex h-9 w-9 items-center justify-center rounded-xl hover:bg-gray-100"
            aria-label="Toggle dark mode"
          >
            <svg className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          </button> */}

          {/* User avatar: initials circle; opens account details */}
          {showUserAvatar && (
            <div className="relative flex-shrink-0" ref={userMenuRef}>
              <button
                type="button"
                onClick={() => setUserMenuOpen((open) => !open)}
                className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-brand-600 text-xs font-medium text-white ring-brand-600/20 transition hover:bg-brand-700 focus:outline-none focus:ring-4 dark:bg-brand-500 dark:hover:bg-brand-600"
                aria-expanded={userMenuOpen}
                aria-haspopup="dialog"
                aria-label={displayNameForInitials ? `Account menu for ${displayNameForInitials}` : 'Account menu'}
              >
                {getInitials(displayNameForInitials)}
              </button>
              {userMenuOpen && (
                <div
                  className="absolute right-0 top-full z-50 mt-2 w-[min(100vw-2rem,22rem)] rounded-base border border-border-base bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-900"
                  role="dialog"
                  aria-label="Account details"
                >
                  {accountEditing ? (
                    <div className="flex flex-col gap-3">
                      <p className="text-xs text-text-body-subtle dark:text-gray-500">
                        Your name appears in the header, on your avatar initials, and on requisitions. Email
                        cannot be changed here.
                      </p>
                      <label className="flex flex-col gap-1.5">
                        <span className="text-xs font-medium text-text-body-subtle dark:text-gray-500">
                          First name
                        </span>
                        <input
                          type="text"
                          autoComplete="given-name"
                          maxLength={NAME_FIELD_MAX_LENGTH}
                          value={editFirstName}
                          onChange={(e) => setEditFirstName(e.target.value)}
                          aria-invalid={editFirstNameInvalid}
                          className={`rounded-base border bg-white px-3 py-2 text-sm font-medium text-text-heading shadow-sm focus:outline-none focus:ring-2 dark:bg-gray-800 dark:text-white ${
                            editFirstNameInvalid
                              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30'
                              : 'border-border-base focus:border-border-brand focus:ring-brand-600/20 dark:border-gray-700 dark:focus:ring-brand-500/30'
                          }`}
                        />
                        {editFirstNameInvalid && (
                          <span className="text-xs text-red-600 dark:text-red-400">
                            {editFirstNameLengthError ?? USER_NAME_INVALID_MESSAGE}
                          </span>
                        )}
                      </label>
                      <label className="flex flex-col gap-1.5">
                        <span className="text-xs font-medium text-text-body-subtle dark:text-gray-500">
                          Middle name <span className="font-normal">(optional)</span>
                        </span>
                        <input
                          type="text"
                          autoComplete="additional-name"
                          maxLength={MIDDLE_NAME_FIELD_MAX_LENGTH}
                          value={editMiddleName}
                          onChange={(e) => setEditMiddleName(e.target.value)}
                          aria-invalid={editMiddleNameInvalid}
                          className={`rounded-base border bg-white px-3 py-2 text-sm font-medium text-text-heading shadow-sm focus:outline-none focus:ring-2 dark:bg-gray-800 dark:text-white ${
                            editMiddleNameInvalid
                              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30'
                              : 'border-border-base focus:border-border-brand focus:ring-brand-600/20 dark:border-gray-700 dark:focus:ring-brand-500/30'
                          }`}
                        />
                        {editMiddleNameInvalid && (
                          <span className="text-xs text-red-600 dark:text-red-400">
                            {editMiddleNameLengthError ?? USER_NAME_INVALID_MESSAGE}
                          </span>
                        )}
                      </label>
                      <label className="flex flex-col gap-1.5">
                        <span className="text-xs font-medium text-text-body-subtle dark:text-gray-500">
                          Last name
                        </span>
                        <input
                          type="text"
                          autoComplete="family-name"
                          maxLength={NAME_FIELD_MAX_LENGTH}
                          value={editLastName}
                          onChange={(e) => setEditLastName(e.target.value)}
                          aria-invalid={editLastNameInvalid}
                          className={`rounded-base border bg-white px-3 py-2 text-sm font-medium text-text-heading shadow-sm focus:outline-none focus:ring-2 dark:bg-gray-800 dark:text-white ${
                            editLastNameInvalid
                              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30'
                              : 'border-border-base focus:border-border-brand focus:ring-brand-600/20 dark:border-gray-700 dark:focus:ring-brand-500/30'
                          }`}
                        />
                        {editLastNameInvalid && (
                          <span className="text-xs text-red-600 dark:text-red-400">
                            {editLastNameLengthError ?? USER_NAME_INVALID_MESSAGE}
                          </span>
                        )}
                      </label>
                      <label className="flex flex-col gap-1.5">
                        <span className="text-xs font-medium text-text-body-subtle dark:text-gray-500">NPI</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          autoComplete="off"
                          maxLength={12}
                          value={editNpi}
                          onChange={(e) => setEditNpi(e.target.value)}
                          onBlur={selfNpiAvailability.validateOnBlur}
                          aria-invalid={editNpiInvalid || selfNpiAvailability.hasDuplicate}
                          className={`rounded-base border bg-white px-3 py-2 text-sm font-medium text-text-heading shadow-sm focus:outline-none focus:ring-2 dark:bg-gray-800 dark:text-white ${
                            editNpiInvalid || selfNpiAvailability.hasDuplicate
                              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30'
                              : 'border-border-base focus:border-border-brand focus:ring-brand-600/20 dark:border-gray-700 dark:focus:ring-brand-500/30'
                          }`}
                        />
                        {editNpiInvalid && (
                          <span className="text-xs text-red-600 dark:text-red-400">{NPI_INVALID_MESSAGE}</span>
                        )}
                        {selfNpiAvailability.isChecking && (
                          <span className="text-xs text-text-body-subtle dark:text-gray-500">Checking NPI availability…</span>
                        )}
                        {selfNpiAvailability.duplicateError && (
                          <span className="text-xs text-red-600 dark:text-red-400">{selfNpiAvailability.duplicateError}</span>
                        )}
                      </label>
                      <label className="flex flex-col gap-1.5">
                        <span className="text-xs font-medium text-text-body-subtle dark:text-gray-500">Phone</span>
                        <input
                          type="tel"
                          autoComplete="tel"
                          value={editPhone}
                          maxLength={PHONE_MAX_LENGTH}
                          onChange={(e) => setEditPhone(e.target.value)}
                          aria-invalid={editPhoneInvalid}
                          className={`rounded-base border bg-white px-3 py-2 text-sm font-medium text-text-heading shadow-sm focus:outline-none focus:ring-2 dark:bg-gray-800 dark:text-white ${
                            editPhoneInvalid
                              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30'
                              : 'border-border-base focus:border-border-brand focus:ring-brand-600/20 dark:border-gray-700 dark:focus:ring-brand-500/30'
                          }`}
                        />
                        {editPhoneInvalid && (
                          <span className="text-xs text-red-600 dark:text-red-400">
                            {!isWithinLength(editPhone, PHONE_MAX_LENGTH)
                              ? PHONE_TOO_LONG_MESSAGE
                              : !isValidFreeText(editPhone)
                                ? FREE_TEXT_INVALID_MESSAGE
                                : PHONE_INVALID_MESSAGE}
                          </span>
                        )}
                      </label>
                      <label className="flex flex-col gap-1.5">
                        <span className="text-xs font-medium text-text-body-subtle dark:text-gray-500">
                          Medical title
                        </span>
                        <input
                          type="text"
                          autoComplete="off"
                          value={editRoleTitle}
                          maxLength={ROLE_TITLE_MAX_LENGTH}
                          placeholder="MD"
                          onChange={(e) => setEditRoleTitle(e.target.value)}
                          aria-invalid={editRoleTitleInvalid}
                          className={`rounded-base border bg-white px-3 py-2 text-sm font-medium text-text-heading shadow-sm placeholder:text-gray-300 focus:outline-none focus:ring-2 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-600 ${
                            editRoleTitleInvalid
                              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30'
                              : 'border-border-base focus:border-border-brand focus:ring-brand-600/20 dark:border-gray-700 dark:focus:ring-brand-500/30'
                          }`}
                        />
                        {editRoleTitleInvalid && (
                          <span className="text-xs text-red-600 dark:text-red-400">
                            {!isWithinLength(editRoleTitle, ROLE_TITLE_MAX_LENGTH)
                              ? ROLE_TITLE_TOO_LONG_MESSAGE
                              : isValidFreeText(editRoleTitle)
                                ? ROLE_TITLE_INVALID_MESSAGE
                                : FREE_TEXT_INVALID_MESSAGE}
                          </span>
                        )}
                      </label>
                      {saveError && (
                        <p className="text-xs font-medium text-red-600 dark:text-red-400" role="alert">
                          {saveError}
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-2 pt-1">
                        <button
                          type="button"
                          onClick={() => void handleSaveProfile()}
                          disabled={
                            saveLoading ||
                            !isDirty ||
                            !editFieldsValid ||
                            selfNpiAvailability.hasDuplicate ||
                            selfNpiAvailability.isChecking
                          }
                          className="rounded-full bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-brand-500 dark:hover:bg-brand-600"
                        >
                          {saveLoading ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          disabled={saveLoading}
                          className="rounded-full border border-border-base px-4 py-2 text-sm font-medium text-text-body hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
                        >
                          Cancel
                        </button>
                        {lastPatchBodyRef.current && saveError && (
                          <button
                            type="button"
                            onClick={handleRetryPatch}
                            disabled={saveLoading}
                            className="rounded-full border border-border-brand px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50 dark:border-brand-500 dark:text-brand-300 dark:hover:bg-gray-800"
                          >
                            Retry
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3">
                      <div className="flex items-start justify-between gap-2">
                        <h2 className="text-sm font-semibold text-text-heading dark:text-white">Account</h2>
                        <button
                          type="button"
                          onClick={() => {
                            setEditFirstName(baselineFirstNameEffective)
                            setEditMiddleName(baselineMiddleNameEffective)
                            setEditLastName(baselineLastNameEffective)
                            setEditNpi(baselineNpiEffective)
                            setEditPhone(baselinePhoneEffective)
                            setEditRoleTitle(baselineRoleTitleEffective)
                            editContactSeedRef.current = {
                              firstName: baselineFirstNameEffective,
                              middleName: baselineMiddleNameEffective,
                              lastName: baselineLastNameEffective,
                              phone: baselinePhoneEffective,
                              roleTitle: baselineRoleTitleEffective,
                            }
                            setSaveError(null)
                            lastPatchBodyRef.current = null
                            setAccountEditing(true)
                          }}
                          className="flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50 dark:text-brand-300 dark:hover:bg-gray-800"
                          aria-label="Edit name, NPI, phone, and medical title"
                        >
                          <Pen className="h-4 w-4" aria-hidden />
                          Edit
                        </button>
                      </div>
                      <dl className="flex flex-col gap-3 text-sm">
                        <div>
                          <dt className="text-xs font-medium text-text-body-subtle dark:text-gray-500">Email</dt>
                          <dd className="mt-0.5 break-all font-medium text-text-body dark:text-gray-300">
                            {loginEmailDisplay}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-text-body-subtle dark:text-gray-500">Name</dt>
                          <dd className="mt-0.5 break-all font-medium text-text-body dark:text-gray-300">
                            {initialsSourceDisplay}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-text-body-subtle dark:text-gray-500">NPI</dt>
                          <dd className="mt-0.5 font-medium text-text-heading dark:text-white">{npiDisplay}</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-text-body-subtle dark:text-gray-500">Phone</dt>
                          <dd className="mt-0.5 break-all font-medium text-text-body dark:text-gray-300">
                            {phoneDisplay}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-text-body-subtle dark:text-gray-500">Medical title</dt>
                          <dd className="mt-0.5 break-all font-medium text-text-body dark:text-gray-300">
                            {roleTitleDisplay}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Logout button */}
          {onLogout && (
            <button
              onClick={onLogout}
              className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              Logout
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default Navbar
